import { ApolloError, gql } from 'apollo-boost';
import c from 'classnames';
import CenterLoader from 'core/components/common/CenterLoader';
import CustomCheckbox from 'core/components/CustomCheckbox';
import { showSnackbar } from 'core/components/snackbar';
import { button } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { omit } from 'core/utils';
import { getErrors } from 'core/utils/apollo';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'site/components/auth/AuthProvider';
import { AccountSubscriptionFragment } from 'site/components/auth/hooks/useGetMyAccount';
import {
    useMailChimpSubscribeMutation,
    useSyncedAccountSubscriptionsMutation,
    useUpdateMyAccountMutation,
} from 'site/graphql';
import useOneSignalPush from './hooks/useOneSignalPush';

gql`
    mutation SyncedAccountSubscriptions {
        syncedAccountSubscriptions {
            id
            accountSubscription {
                ...AccountSubscriptionFragment
            }
        }
    }
    ${AccountSubscriptionFragment}
`;

gql`
    mutation MailChimpSubscribe {
        subscribe {
            id
            accountSubscription {
                ...AccountSubscriptionFragment
            }
        }
    }
    ${AccountSubscriptionFragment}
`;

const NotificationsContent: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const [isDirty, setIsDirty] = useState(false);
    const { myAccount } = useAuth();
    const [account, setAccount] = useState(myAccount);
    const [mailchimpStatusError, setMailchimpStatusError] = useState(null);
    const { pushLoading, pushNotifications, pushButtonDisabled, onPushNotificationsChange } = useOneSignalPush();

    const [updateAccount, { loading }] = useUpdateMyAccountMutation({
        onCompleted: () => showSnackbar({ snackbarMessage: 'Изменения сохранены' }),
        onError: (error: ApolloError) => showSnackbar({ snackbarErrors: getErrors(error) }),
    });
    const [sync] = useSyncedAccountSubscriptionsMutation({
        onError: (error: ApolloError) => setMailchimpStatusError(getErrors(error)),
    });
    const [subscribe] = useMailChimpSubscribeMutation({
        onCompleted: () => showSnackbar({ snackbarMessage: 'Изменения сохранены' }),
        onError: (error: ApolloError) => setMailchimpStatusError(getErrors(error)),
    });

    useEffect(() => {
        // Load and sync MailChimp subscriptions
        sync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCheckboxChange = useCallback(
        (fieldName: string) => (checked: boolean) => {
            setAccount(acc => ({ ...acc, accountSubscription: { ...acc.accountSubscription, [fieldName]: checked } }));
            setIsDirty(true);
        },
        [],
    );

    const handleSubscribe = useCallback(() => {
        subscribe();
        setIsDirty(false);
    }, [subscribe]);

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { __typename, ...accountSubscription } = account.accountSubscription;

            updateAccount({
                variables: {
                    account: {
                        firstName: account.firstName,
                        lastName: account.lastName,
                        middleName: account.middleName,
                        phones: account.phones,
                        telegramUserName: account.telegramUserName,
                        vkLink: account.vkLink,
                        facebookLink: account.facebookLink,
                        accountSubscription: omit(accountSubscription, 'id'),
                    },
                },
            });

            setIsDirty(false);
        },
        [account, updateAccount],
    );

    const renderCheckBox = useCallback(
        (field: string, title: string) => {
            return (
                <CustomCheckbox
                    onChange={handleCheckboxChange(field)}
                    checked={account.accountSubscription[field]}
                    label={title}
                />
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [account],
    );

    const errorMessage = mailchimpStatusError && mailchimpStatusError[0] && mailchimpStatusError[0].message;
    const blockResend = errorMessage && !errorMessage.includes('Подписаться');

    const {
        nearestCoursesSubscription,
        weekCoursesSubscription,
        mondayRegularSubscription,
        thursdayRegularSubscription,
        newCourseSubscription,
    } = account.accountSubscription;
    const blockSubscriptions =
        !isDirty &&
        !newCourseSubscription &&
        !nearestCoursesSubscription &&
        !weekCoursesSubscription &&
        !mondayRegularSubscription &&
        !thursdayRegularSubscription;

    return (
        <form onSubmit={handleSubmit} className={classes.form}>
            {(pushLoading || loading) && <CenterLoader absolute />}
            {errorMessage && <div className={classes.resubscribeInfo}>{errorMessage}</div>}
            <div className={classes.containerNotifications}>
                <div className={classes.columnNotification}>
                    <div className={classes.titleText}>Включите напоминания</div>
                    {renderCheckBox('notPayedNotify', 'Об оплате курса')}
                    {renderCheckBox('courseStartNotify', 'О начале курса')}
                    {renderCheckBox('courseSuccessRegistrationNotify', 'Об успешной регистрации на курс')}
                    {renderCheckBox('service52Weeks', '52 недели богатства')}

                    <div className={c(classes.titleText, classes.otherTitle)}>Прочие рассылки</div>
                    {renderCheckBox('welcomeEmails', 'Приветственные письма')}
                    {!pushButtonDisabled && (
                        <CustomCheckbox
                            onChange={onPushNotificationsChange}
                            checked={pushNotifications}
                            label="Пуш-уведомления"
                        />
                    )}
                </div>
                <div className={classes.columnNotification}>
                    <div className={classes.titleText}>Получайте уведомления на электронную почту</div>
                    {blockSubscriptions ? (
                        <>
                            <div>У вас еще нет ни одной подписки</div>
                            <button
                                className={classes.button}
                                onClick={handleSubscribe}
                                disabled={blockResend}
                                style={{ marginTop: '0.5rem' }}
                            >
                                Подписаться
                            </button>
                        </>
                    ) : (
                        <>
                            {/* ВАЖНО! В этом блоке только MailChimp подписки */}
                            {renderCheckBox('newCourseSubscription', 'О новых курсах на сайте')}
                            {renderCheckBox('nearestCoursesSubscription', 'О ближайших курсах')}
                            {renderCheckBox('weekCoursesSubscription', 'О курсах на неделю')}
                            {renderCheckBox('mondayRegularSubscription', 'Еженедельная рассылка по понедельникам')}
                            {renderCheckBox('thursdayRegularSubscription', 'Еженедельная рассылка по четвергам')}
                        </>
                    )}
                </div>
            </div>
            <button className={classes.button} type="submit">
                Применить
            </button>
        </form>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        form: {
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 1 + 'rem',
            position: 'relative',

            [theme.breakpoints.down('xs')]: {
                width: 'calc(100% - 2rem)',
                marginBottom: 1 + 'rem',
            },
        },
        resubscribeInfo: {
            color: '#8a6d3b',
            textAlign: 'center',
            backgroundColor: '#fcf8e3',
            border: '1px solid #faebcc',
            borderRadius: 4,
            padding: 0.5 + 'rem',
            fontSize: 0.9 + 'rem',
            marginBottom: 0.5 + 'rem',
        },
        containerNotifications: {
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,

            [theme.breakpoints.down('xs')]: {
                flexDirection: 'column',
            },
        },
        titleText: {
            height: 60,
            fontWeight: 600,
            fontSize: 1 + 'rem',

            [theme.breakpoints.down('xs')]: {
                height: 'auto',
            },
        },
        otherTitle: {
            marginTop: 30,

            [theme.breakpoints.down('xs')]: {
                marginTop: 0,
            },
        },
        columnNotification: {
            width: 50 + '%',

            [theme.breakpoints.down('xs')]: {
                width: 100 + '%',
                marginBottom: 1 + 'rem',
            },
        },
        button: {
            ...button(theme),
            alignSelf: 'center',
            marginTop: 2 + 'rem',
            border: '1px solid #666',
            fontWeight: 600,
            paddingLeft: 1.5 + 'rem',
            paddingRight: 1.5 + 'rem',

            '&:disabled': {
                background: '#eee',
                color: '#8a8989',
                borderColor: '#8a8989',
                cursor: 'default',
            },

            '&:focus': {
                outline: 'none',
            },
        },
    });

export default injectSheet(styles, 'NotificationsContent')(NotificationsContent);
